/**
 * @fileoverview Session Store for Virtual Test Center (VTC)
 * 
 * This Zustand store manages the state and lifecycle of exam sessions in the Virtual Test Center.
 * It handles session transitions between different states (waiting, review, testing, checkout, reconnect),
 * tracks focused sessions, and manages media connections for live proctoring.
 * 
 * @module vtc/stores/session
 * @requires zustand
 * @requires ./session-dummy-data
 * @requires ./app
 * @requires ./dataBroker
 * 
 * @example
 * // Import and use the store
 * import useSessionStore from './stores/session';
 * 
 * function MyComponent() {
 *   const sessions = useSessionStore((state) => state.sessions);
 *   const addSession = useSessionStore((state) => state.add);
 *   
 *   // Add a new session
 *   addSession('session_123', { location: 'waiting', studentName: 'John Doe' });
 * }
 */

import { create } from 'zustand';
import React from 'react';
import dummySessions from './session-dummy-data';
import useAppStore from './app';
import useLayoutStore from './layout';
import useDataBroker from './dataBroker';
import config from '../config';
import { useDeviceData } from 'react-device-detect';
import { transferSession, setSessionAsCheckedIn } from '../lib/api';
import { PROCTOR_ROOM_EVENTS } from '@smarterservices/sync-client';

/**
 * Session Store
 * 
 * @typedef {Object} SessionState
 * @property {number} count - Total count of sessions (deprecated, not actively used)
 * @property {boolean} initialStateLoaded - Flag indicating if initial session state has been loaded
 * @property {string|null} transferSessionSid - Session ID of the session being transferred
 * @property {string|null} endSessionSid - Session ID of the session being ended
 * @property {Object|null} focusedSession - The currently focused session data
 * @property {string|null} focusedSessionLocation - Location of the focused session (deprecated)
 * @property {string|null} focusedSessionSid - Session ID of the currently focused session
 * @property {number} testingCount - Count of sessions currently in testing state
 * @property {Object.<string, {location: string}>} sessions - Map of all active sessions by SID
 * @property {Object.<string, Object>} reconnect - Sessions in reconnect state
 * @property {Object.<string, Object>} waiting - Sessions in waiting state (waiting for proctor)
 * @property {Object.<string, Object>} review - Sessions in review state (proctor reviewing)
 * @property {Object.<string, Object>} testing - Sessions in testing state (actively taking exam)
 * @property {Object.<string, Object>} checkout - Sessions in checkout state (exam completed)
 * 
 * @typedef {Object} SessionActions
 * @property {Function} updateLoader - Marks initial state as loaded
 * @property {Function} add - Adds a new session to the store
 * @property {Function} remove - Removes a session from the store
 * @property {Function} handleRemoveEvent - Internal handler for removing sessions
 * @property {Function} addRandom - Adds a random dummy session (testing only)
 * @property {Function} getSessionData - Retrieves session data by session ID
 * @property {Function} setTransferSessionSid - Sets the session ID for transfer
 * @property {Function} transferSession - Executes session transfer
 * @property {Function} setEndSessionSid - Sets the session ID to end
 * @property {Function} endSession - Ends a session
 * @property {Function} clearFocusedSession - Clears the currently focused session
 * @property {Function} getFocusedSessionSid - Gets the focused session ID
 * @property {Function} transitionSession - Transitions a session to a new state
 * @property {Function} handleTransitionEvent - Internal handler for state transitions
 * @property {Function} setFocusedSession - Sets a session as focused
 * @property {Function} getFocusedSession - Gets the currently focused session data
 * @property {Function} persistChange - Persists changes (placeholder for future implementation)
 */

const useSessionStore = create((set, get) => ({
  // ============================================================================
  // STATE
  // ============================================================================

  /** @type {number} Total count of sessions (deprecated) */
  count: 0,

  /** @type {boolean} Flag indicating if initial session state has been loaded */
  initialStateLoaded: false,

  /** @type {string|null} Session ID of the session being transferred */
  transferSessionSid: null,

  /** @type {string|null} Session ID of the session being cleaned up, this allows us not to run certain functions. */
  cleaningUpSessionSid: null,

  /** @type {boolean} Flag to show the transfer note modal for the focused session */
  showTransferNote: false,

  /** @type {string|null} Session ID of the session being ended */
  endSessionSid: null,

  /** @type {Object|null} The currently focused session data */
  focusedSession: null,

  /** @type {string|null} Location of the focused session (deprecated) */
  focusedSessionLocation: null,

  /** @type {string|null} Session ID of the currently focused session */
  focusedSessionSid: null,

  /** @type {Object|null} Claim failure details when a session claim is lost */
  claimFailure: null,

  /** @type {number} Count of sessions currently in testing state */
  testingCount: 0,

  /** @type {Object.<string, {location: string}>} Map of all active sessions */
  sessions: {},

  /** @type {Object.<string, Object>} Sessions in reconnect state */
  reconnect: {},

  /** @type {Object.<string, Object>} Sessions in waiting state */
  waiting: {},

  /** @type {Object.<string, Object>} Sessions in review state */
  review: {},

  /** @type {Object.<string, Object>} Sessions in sentback state, meaning they were in onboarding and were asked to go back and the proctor disconnected */
  sentback: {},

  /** @type {Object.<string, Object>} Sessions in testing state */
  testing: {},

  /** @type {Object.<string, Object>} Sessions in checkout state */
  checkout: {},

  flaggingAlerts: {}, // will hold the session and then each session object will have and object with unseenCount.

  /** 
   * @type {Array<{sessionSid: string, inRoomAt: number}>} 
   * Array of sessions that should be displayed in the SessionGrid.
   * Contains sessions in testing location and sessions with recent connection changes
   * that haven't been acknowledged yet (ended or transferred).
   * Sorted by inRoomAt timestamp (oldest first).
   */
  visibleSessions: [],

  /** 
   * @type {Object.<string, {hasNoise: boolean, audioLevel: number, participantInfo: Object}>}
   * Map of sessions that currently have noise detected.
   * Only contains sessions where hasNoise is true - sessions are removed when noise stops.
   * Stored separately from main sessions object to avoid triggering re-renders when updating
   * noise state, as the sessions object contains non-serializable LiveKit objects and React refs.
   * 
   * @property {boolean} hasNoise - Always true for entries in this object
   * @property {number} audioLevel - Current audio level of the participant
   * @property {Object} participantInfo - Information about the participant causing noise
   * @property {string} participantInfo.identity - Participant's identity
   * @property {string} participantInfo.id - Participant's ID
   * @property {string} participantInfo.participantSid - Participant's SID
   * @property {string} participantInfo.type - Participant type (e.g., 'student', 'proctor')
   */
  noisySessions: {},

  /** 
   * @type {Array<string>} 
   * Array of session SIDs that have been checked out and acknowledged by the proctor.
   * Used to prevent ended sessions from reappearing in the visible sessions array
   * after they've been acknowledged.
   */
  acknowledgedSessions: [],

  /** 
   * @type {Object.<string, Object>} 
   * Object containing sessions that have exited the room. This will basically be an in memory snapshot of the session that will be removed when acknowledged.
   * Keyed by session SID, stores full session object that was in sessions object.
   */
  exitedSessions: {},

  /** 
   * @type {Object.<string, Object.<string, Object>>} 
   * Object containing user interaction data tracked in the sync document for each session.
   * Used to track proctor interactions with sessions (e.g., start-review, start-focus, approved, etc.).
   * 
   * Structure: Keyed by session SID, each containing user interaction data keyed by user key SID.
   * 
   * @example
   * {
   *   'ES123abc': {
   *     'PU456def': {
   *       interactionType: 'start-review',
   *       type: 'instructor',
   *       timestamp: 1704358800000,
   *       updatedAt: 1704358800000
   *     },
   *     'PU789ghi': {
   *       interactionType: 'approved',
   *       type: 'proctor',
   *       timestamp: 1704358900000,
   *       updatedAt: 1704358900000
   *     }
   *   },
   *   'ES456def': {
   *     'PU456def': {
   *       interactionType: 'start-focus',
   *       type: 'support',
   *       timestamp: 1704359000000,
   *       updatedAt: 1704359000000
   *     }
   *   }
   * }
   */
  userInteractions: {},

  /** @type {React.MutableRefObject|null} Reference to removeSessionViewer function from SessionViewerProvider */
  removeSessionViewerRef: null,

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Marks the initial state as loaded
   * 
   * This should be called after the initial session data has been loaded from the server
   * or data source. It enables features like sound notifications for new sessions.
   * 
   * @function updateLoader
   * @returns {void}
   * 
   * @example
   * useSessionStore.getState().updateLoader();
   */
  updateLoader: () =>
    set(() => {
      return {
        initialStateLoaded: true,
      };
    }),

  /**
   * Adds a new session to the store
   * 
   * Adds a session to the appropriate location bucket (waiting, review, testing, checkout, reconnect)
   * and plays a sound notification if the session is added to the waiting queue after initial load.
   * 
   * @function add
   * @param {string} sessionSid - The unique session identifier (SID)
   * @param {Object} data - The session data object
   * @param {string} data.location - The location/state of the session. Valid values: "waiting", "review", "testing", "checkout", "reconnect"
   * @param {string} [data.studentName] - Name of the student
   * @param {string} [data.examName] - Name of the exam
   * @param {Object} [data...] - Additional session properties
   * @returns {void}
   * 
   * @example
   * // Add a session to the waiting queue
   * useSessionStore.getState().add('session_123', {
   *   location: 'waiting',
   *   studentName: 'John Doe',
   *   examName: 'Math 101 Final'
   * });
   * 
   * @example
   * // Add a session directly to testing
   * useSessionStore.getState().add('session_456', {
   *   location: 'testing',
   *   studentName: 'Jane Smith'
   * });
   */
  add: (sessionSid, data) => {

    // If all the critical data is not in the data object
    if (!data?.location ||
      !data?.installSid ||
      !data?.examSid ||
      !data?.firstName ||
      !data?.lastName) {

      console.trace(`[VTC] Omit adding student for ${sessionSid} - missing critical data`, {
        hasLocation: !!data?.location,
        hasInstallSid: !!data?.installSid,
        hasExamSid: !!data?.examSid,
        hasFirstName: !!data?.firstName,
        hasLastName: !!data?.lastName
      });

      return; // Exit early, don't add session
    }

    set((state) => {
      const user = useAppStore.getState().user;

      // We are not going to build the session at this point, this is just to create the base props that will be used as the basis for the connection...
      const sessionProps = {
        sessionSid,
        installSid: data?.installSid,
        examSid: data?.examSid,
        isReconnect: data?.isReconnect || false, // Pass isReconnect from proctor sync doc to SessionViewer
        apiUrl: config.proctoring?.api?.baseUrl,
        websocketUrl: config.media?.livekit?.websocketUrl,
        roomId: sessionSid,
        spToken: user.token,
        identity: user.identity,
        attachedToProctorRoomSid: useAppStore.getState().roomSid, // Set the current room SID
        onSessionLoaded: get().handleSessionLoaded,
        onSessionReady: get().handleSessionReady,
        onUnreadMessageCountChange: get().handleOnUnreadMessageCountChange,
        onHandRaisedChange: get().handleOnHandRaisedChange,
        //onFlaggingChange: handleFlagCountChange,
        onConnected: get().handleOnSessionConnected,
        onDisconnected: () => get().handleOnSessionDisconnected,
        onSessionApproved: get().handleSessionApproved,
        onSettingsChange: get().handleSettingsChange,
        //onReject: handleReject,
        onNoiseChange: get().handleOnNoiseChange,
        onSentBackToOnboardingStep: get().handleSentBackToOnboardingStep,
        onLastConnectionChangeUpdated: get().handleLastConnectionChangeUpdated,
        onLastConnectionChangeAcknowledgement: get().handleLastConnectionChangeAcknowledgement,
        onSessionEnded: get().handleOnSessionEnded,
        onFlaggingChange: get().handleFlaggingChange,
        onUserInteraction: get().handleUserInteraction,
        type: "proctor",
        enabledComponents: {
          sessionDetails: false,
          sessionHeader: false,
          allowFullScreen: false,
          microphone: false,   // Proctor doesn't need mic
          camera: false,       // Proctor doesn't need camera
          chat: false,         // Chat handled separately
          screenShare: false,  // Not needed in card view
          settings: true,     // Not needed in card view
          leave: false,        // Not needed in card view
        },
        video: {
          sources: ['live', 'recorded'],  // Can view live or recorded
          default: 'live',                // Default to live view
        },
      };

      // Create a ref for the SessionViewer component to access its methods
      const viewerRef = React.createRef();

      // Prepare the session metadata to add to sessions object
      const sessionMetadata = {
        location: data.location,
        viewerProps: sessionProps, // Store props for dynamic viewer creation
        viewerRef, // Ref to access SessionViewer methods like endSession(), this will be added from the SessionViewerContext
        sessionData: data,
      };

      // Only update the specific location object where the session is being added
      switch (data.location) {
        case 'waiting': {
          // Trigger sound, but only after the initial state has been loaded
          if (get().initialStateLoaded && useAppStore.getState().allowSounds) {
            // Use setTimeout to ensure state update happens after this action completes
            setTimeout(() => {
              useAppStore.getState().playSound('new-session');
            }, 0);
          }

          return {
            sessions: { ...state.sessions, [sessionSid]: sessionMetadata },
            waiting: { ...state.waiting, [sessionSid]: data },
          };
        }
        case 'review':
          return {
            sessions: { ...state.sessions, [sessionSid]: sessionMetadata },
            review: { ...state.review, [sessionSid]: data },
          };
        case 'testing':
          return {
            sessions: { ...state.sessions, [sessionSid]: sessionMetadata },
            testing: { ...state.testing, [sessionSid]: data },
            testingCount: Object.keys(state.testing).length + 1,
          };
        case 'checkout':
          return {
            sessions: { ...state.sessions, [sessionSid]: sessionMetadata },
            checkout: { ...state.checkout, [sessionSid]: data },
          };
        case 'reconnect':
          // Trigger sound, but only after the initial state has been loaded
          if (get().initialStateLoaded && useAppStore.getState().allowSounds) {
            // Use setTimeout to ensure state update happens after this action completes
            setTimeout(() => {
              useAppStore.getState().playSound('new-session');
            }, 0);
          }
          return {
            sessions: { ...state.sessions, [sessionSid]: sessionMetadata },
            reconnect: { ...state.reconnect, [sessionSid]: data },
          };
        case 'sentback':
          return {
            sessions: { ...state.sessions, [sessionSid]: sessionMetadata },
            sentback: { ...state.sentback, [sessionSid]: data },
          };
        default:
          // Unknown location, no state update needed
          return {};
      }
    })
    get().updateVisibleSessions();
  },

  handleOnSessionConnected: async ({ sessionSid, participantRegistrationData }) => {
    // Session connected - participant registration data available
  },


  handleUserInteraction: async ({ sessionSid, current, previous }) => {
    // update the userInteraction object with the current data, keyed by sessionSid
    set((state) => ({
      userInteractions: {
        ...state.userInteractions,
        [sessionSid]: current
      }
    }));

  },

  handleSessionLoaded: async ({ sessionData }) => {
    // get the current session location 
    const session = get().sessions[sessionData.sid];
    // Start: This is the worst case situation where we are making sure the session is in the right place. The agent should really be handling this cleanup for us anyway...
    // if the status in session data is either closed or cancelled, we need to ensure this is in the right place.
    if (sessionData.status.toLowerCase() === 'closed' || sessionData.status.toLowerCase() === 'cancelled') {
      // transition the session to checkout
      if (session.location !== 'checkout') {

        get().transitionSession(sessionData.sid, 'checkout');
      }
    }
    // End: session cleanup logic

    // if the session is in review, and the proctor is currently focused on this session, then update the proctor interaction to start the review
    if (session && session.location === 'review' && sessionData.sid === get().focusedSessionSid) {
      get().updateProctorInteraction(sessionData.sid, 'start-review');
    }
  },

  /**
   * Handles session viewer ready event
   * 
   * Called when a session viewer is fully initialized and ready. This function:
   * 1. Loads all user interactions from the sync document
   * 2. Determines if current proctor should be reviewer or just focused viewer
   * 3. Updates proctor interaction accordingly (start-review or start-focus)
   * 4. Removes checkin tab for secondary viewers (non-reviewers)
   * 
   * The session.reviewBy field is used as the source of truth to determine who
   * should be the primary reviewer. Only one proctor should have inReview: true.
   * 
   * @async
   * @function handleSessionReady
   * @param {Object} params - Event parameters
   * @param {Object} params.sessionData - Session data from the viewer
   * @param {string} params.sessionData.sid - Session identifier
   * @returns {Promise<void>}
   * 
   * @example
   * // Called automatically by SessionViewer when ready
   * onSessionReady: get().handleSessionReady
   */
  handleSessionReady: async ({ sessionData }) => {
    const session = get().sessions[sessionData.sid];
    // Session is now fully initialized and the ref is available
    // Load all user interactions from the sync document
    await get().loadAllUserInteractions(sessionData.sid);

    // If this is the focused session, update interaction based on whether someone else is already reviewing
    if (session && sessionData.sid === get().focusedSessionSid) {
      if (session.location === 'review') {
        // Check the session's reviewBy field - this is the source of truth
        const currentUserSid = useAppStore.getState()?.user?.sid;
        const proctors = useAppStore.getState().proctors;
        const reviewerIsOnline = session.sessionData?.reviewBy && proctors[session.sessionData.reviewBy];

        // If the reviewBy is not the current user, remove the checkin tab
        if (session.sessionData?.reviewBy !== currentUserSid) {
          const sessionViewerRef = get().getSessionViewerRef(sessionData.sid);
          if (sessionViewerRef?.removeSessionDetailsTabs) {
            sessionViewerRef.removeSessionDetailsTabs(['checkin']);
          }
        }

        if (!session.sessionData?.reviewBy || !reviewerIsOnline || session.sessionData?.reviewBy === currentUserSid) {
          // No reviewer assigned, or reviewer is offline, or it's us - set inReview
          await get().updateProctorInteraction(sessionData.sid, 'start-review');
        } else {
          // Someone else is the assigned reviewer and online - just focus
          await get().updateProctorInteraction(sessionData.sid, 'start-focus');
        }
      } else {
        // For other locations, just set focus
        await get().updateProctorInteraction(sessionData.sid, 'start-focus');
      }
    }
  },

  /**
   * Loads all user interactions for a session from the sync document
   * 
   * Retrieves the complete userInteractions object from the session's sync document
   * and stores it in the session store. This includes all proctors' focus, review,
   * and talking states for the session.
   * 
   * @async
   * @function loadAllUserInteractions
   * @param {string} sessionSid - The session identifier
   * @returns {Promise<void>}
   * 
   * @example
   * await useSessionStore.getState().loadAllUserInteractions('ES123');
   */
  loadAllUserInteractions: async (sessionSid) => {
    const sessionViewerRef = get().getSessionViewerRef(sessionSid);
    if (sessionViewerRef?.getAllUserInteractions) {
      const interactions = await sessionViewerRef.getAllUserInteractions();
      set((state) => ({
        userInteractions: {
          ...state.userInteractions,
          [sessionSid]: interactions
        }
      }));
    }
  },

  /**
   * Handles session approval workflow
   * 
   * Processes a session that has been approved during check-in. Updates the proctor
   * interaction to mark the check-in as complete and transitions the session to the
   * testing state.
   * 
   * @async
   * @function handleSessionApproved
   * @param {Object} params - Session approval parameters
   * @param {string} params.sessionSid - Session identifier
   * @param {string} params.installSid - Installation identifier
   * @param {string} params.examSid - Exam identifier
   * @returns {Promise<void>}
   * 
   * @example
   * await handleSessionApproved({
   *   sessionSid: 'sess_123',
   *   installSid: 'inst_456',
   *   examSid: 'exam_789'
   * });
   */
  handleSessionApproved: async ({ sessionSid, installSid, examSid }) => {
    // Since the session was approved, update the interaction to indicate that checkin has ended...
    await get().updateProctorInteraction(sessionSid, 'approved');
    // transition the session to testing...
    await get().transitionSession(sessionSid, 'testing');
    // Call the API to set the session status as checked in...
    await setSessionAsCheckedIn(sessionSid);
    // call to enable the raise hand button...
    await get().invokeSessionAction({
      sessionSid, data: {
        action: 'updateToolbarButtons',
        enabled: true,
        buttonNames: [
          'raiseHand'
        ]
      }
    });
  },

  handleFlaggingChange: ({ sessionSid, installSid, examSid, newFlag }) => {
    let shouldIncrement = false;

    if (newFlag?.type?.categoryId === 'flags') {
      if (
        // range type...
        (newFlag?.type?.type === 'range' && (newFlag?.range?.end === '' || newFlag?.range?.end === null))
        // point type...
        || (newFlag?.type?.type === 'point')
      ) {
        shouldIncrement = true;
      }

      if (shouldIncrement) {
        const currentAlerts = get().flaggingAlerts;
        const currentCount = currentAlerts[sessionSid]?.unseenCount || 0;

        set({
          flaggingAlerts: {
            ...currentAlerts,
            [sessionSid]: { unseenCount: currentCount + 1 }
          }
        });
      }
    }
  },

  clearFlaggingAlerts: (sessionSid) => {
    const currentAlerts = get().flaggingAlerts;
    const { [sessionSid]: removed, ...remainingAlerts } = currentAlerts;
    set({ flaggingAlerts: remainingAlerts });
  },

  handleSettingsChange: ({ sessionSid, installSid, examSid, change }) => {

    // Prevent recursive updates - if we're already propagating settings, don't propagate again
    if (get()._isPropagatingSettings) {
      return;
    }

    // when the settings change and the change is audio as defined by having the key change.audio, loop all sessions in the pool and update their settings as well...
    if (change?.audio) {
      // Set flag to prevent recursion
      set({ _isPropagatingSettings: true });

      try {
        const sessions = get().sessions;
        Object.values(sessions).forEach(session => {
          if (session.sessionData?.sessionSid && session.sessionData?.sessionSid !== sessionSid) {
            if (session?.viewerRef?.current && session.viewerRef.current.updateSettings) {
              try {
                session.viewerRef.current.updateSettings(change);
              } catch (error) {
                console.error('Error updating settings for session', session.sessionSid, ':', error);
              }
            }
          }
        });
      } finally {
        // Always clear the flag, even if there's an error
        set({ _isPropagatingSettings: false });
      }
    }
  },

  handleLastConnectionChangeUpdated: async ({ sessionSid, current, type }) => {
    if (type === 'ended') {
      get().addToExitedSessions(sessionSid);
    }
  },

  handleLastConnectionChangeAcknowledgement: async ({ sessionSid, installSid, examSid, change }) => {
    // based on the change type and info we need to do different things...
    if (change.type === 'transferred') {

      // if this is the room the session was transferred into, do nothing...
      if (change?.metadata?.toProctorRoomSid === useAppStore.getState().roomSid) {
        return;
      } else {
        // so we know that we are handling transfer cleanup, set it to true
        set({ cleaningUpSessionSid: sessionSid });


        // if this is the room the session was transferred from...
        get().handleRemoveEvent(sessionSid);

        // unfocus the session if it's currently focused
        if (get().focusedSessionSid === sessionSid) {

          useAppStore.getState().setDrawerCloseConfirmation(null);

          await useAppStore.getState().closeDrawer();

          set({
            transferSessionSid: null,
            transferNoteSessionSid: null,
            endSessionSid: null,
            focusedSession: null,
            cleaningUpSessionSid: null
          });
          useAppStore.getState().setAllowSounds(true);

        }
      }
    } else if (change.type === 'ended') {
      // so we know that we are handling transfer cleanup, set it to true
      set({ cleaningUpSessionSid: sessionSid });

      // if the session is not in the exitedSessions object, add it. It is possible it gets to the acknowledgement before it gets to the ended state...
      if (!get().exitedSessions[sessionSid]) {
        get().addToExitedSessions(sessionSid);
      }

      // if this session is the focused session, clearn the focused session...
      if (get().focusedSessionSid === sessionSid) {

        useAppStore.getState().setDrawerCloseConfirmation(null);

        await useAppStore.getState().closeDrawer();

        set({
          transferSessionSid: null,
          transferNoteSessionSid: null,
          endSessionSid: null,
          focusedSession: null,
          cleaningUpSessionSid: null
        });
        useAppStore.getState().setAllowSounds(true);
      }

      // since this has been acknowledged, remove it from the exited sessions...
      get().removeFromExitedSessions(sessionSid);
      // transition session to checout, this will also update the visible sessions...
      await get().handleTransitionEvent(sessionSid, 'checkout');
    }

    // disconnect the session viewer...
    const sessionViewerRef = get().getSessionViewerRef(sessionSid);
    if (sessionViewerRef?.disconnect) {
      sessionViewerRef.disconnect();
    }

  },

  addAcknowledgedSession: (sessionSid) => {
    const acknowledgedSessions = get().acknowledgedSessions;
    const exitedSessions = get().exitedSessions;
    console.log('>> adding acknowledged session', acknowledgedSessions);
    // Clean up acknowledged sessions that are no longer in exitedSessions
    // This prevents the acknowledgedSessions array from bloating over time
    const cleanedAcknowledgedSessions = acknowledgedSessions.filter(
      (sid) => exitedSessions.includes(sid)
    );

    // Only add if not already present to prevent duplicates
    if (!cleanedAcknowledgedSessions.includes(sessionSid)) {
      set({ acknowledgedSessions: [...cleanedAcknowledgedSessions, sessionSid] });
    } else if (cleanedAcknowledgedSessions.length !== acknowledgedSessions.length) {
      // Update state even if not adding new session, to persist the cleanup
      set({ acknowledgedSessions: cleanedAcknowledgedSessions });
    }
  },

  handleOnSessionDisconnected: ({ sessionSid }) => {
  },

  handleOnNoiseChange: ({ sessionSid, hasNoise, audioLevel, participantInfo }) => {
    // only do this if the sesion is not focused...
    if (get().focusedSessionSid === sessionSid) {
      return;
    }

    if (hasNoise) {
      // Add to noisySessions if noise is detected
      set({
        noisySessions: {
          ...get().noisySessions,
          [sessionSid]: {
            hasNoise: true,
            audioLevel,
            participantInfo
          }
        }
      });
    } else {
      // Remove from noisySessions if no noise
      const { [sessionSid]: removed, ...remainingNoisySessions } = get().noisySessions;
      set({ noisySessions: remainingNoisySessions });
    }
  },

  handleOnUnreadMessageCountChange: ({ sessionSid, count }) => {
    // update the value in the sessions object only for this specific session

    // if the existing state is 0 for this session and the new state is 1, play a sound
    if (count === 1) {
      useAppStore.getState().playSound('new-message');
    }

    set((state) => ({
      sessions: {
        ...state.sessions,
        [sessionSid]: {
          ...state.sessions[sessionSid],
          unreadMessageCount: count
        }
      }
    }));
  },

  handleOnHandRaisedChange: async ({ sessionSid, raised }) => {
    // if this session is currently focused, we need to lower the hand right away and return back out...
    if (raised) {
      if (get().focusedSessionSid === sessionSid) {
        await get().lowerRaisedHand({ sessionSid, reason: "Session already in focused mode." });
        raised = false;
      } else {
        // play the hand raised sound
        useAppStore.getState().playSound('hand-raised');
      }
    }
    // update the value in the sessions object only for this specific session
    set((state) => ({
      sessions: {
        ...state.sessions,
        [sessionSid]: {
          ...state.sessions[sessionSid],
          handRaised: raised
        }
      }
    }));
  },

  isHandRaised: (sessionSid) => {
    return get().sessions?.[sessionSid]?.handRaised || false;
  },

  /**
   * Updates the visibleSessions array with sessions that should be displayed in the grid
   * 
   * This function builds the visible sessions array in multiple stages:
   * 
   * **Stage 1: Retain Previously Visible Sessions with Active Connections**
   * - Loops through existing visibleSessions to check if they should be retained
   * - Checks if session has ended (via getLastConnectionChange)
   * - Excludes sessions that have been acknowledged (in acknowledgedCheckedOutSessions)
   * - Retains sessions with type 'ended' that haven't been acknowledged yet
   * - Tracks retained sessions in alreadyAddedSessions to prevent duplicates
   * 
   * **Stage 2: Add Testing Sessions**
   * - Loops through all sessions in the store
   * - Adds sessions with location 'testing' to the visible array
   * - Skips sessions already added in Stage 1 (checks alreadyAddedSessions)
   * - Each session is stored as an object: { sessionSid, inRoomAt }
   * 
   * **Stage 3: Sort by Timestamp**
   * - Sorts all visible sessions by inRoomAt timestamp
   * - Oldest sessions appear first (ascending order)
   * 
   * The resulting array contains session objects with sessionSid and inRoomAt properties,
   * which are used by SessionGrid to render sessions in the correct order and by
   * SessionViewerPool to determine which sessions to keep mounted.
   * 
   * @function updateVisibleSessions
   * @returns {void}
   * 
   * @example
   * // Manually trigger visible sessions update
   * useSessionStore.getState().updateVisibleSessions();
   * 
   * @example
   * // Automatically called when:
   * // - A session is added to the store
   * // - A session transitions between locations
   * // - A session's connection change is acknowledged
   */
  updateVisibleSessions: () => {
    const sessions = get().sessions;
    const visibleSessionsArray = [];
    const alreadyAddedSessions = [];

    const exitedSessionKeys = Object.keys(get().exitedSessions);
    const acknowledgedSessions = get().acknowledgedSessions;

    if (exitedSessionKeys.length > 0) {
      //loop them
      for (const sessionSid of exitedSessionKeys) {
        // Skip sessions that have already been acknowledged
        if (acknowledgedSessions.includes(sessionSid)) {
          continue;
        }

        const exitedSession = get().exitedSessions[sessionSid];
        const session = get().getSession(sessionSid);

        // Skip sessions that should never be visible based on location
        const invalidLocations = ['waiting', 'reconnect', 'review', 'sentback'];
        if (invalidLocations.includes(session?.location)) {
          continue;
        }

        if (session?.viewerRef?.current) {
          try {
            const lastConnectionChange = session.viewerRef.current.getLastConnectionChange();

            visibleSessionsArray.push({
              sessionSid: sessionSid,
              inRoomAt: exitedSession.inRoomAt || 0
            });
            alreadyAddedSessions.push(sessionSid);

          } catch (error) {
            console.error('Failed to invoke action:', error);
            return null;
          }
        }
      }
    }

    // Stage 1: Add all testing sessions
    for (const [sessionSid, session] of Object.entries(sessions)) {
      const sessionDetails = get().getSessionData(sessionSid);
      // ensure this session is. not already in the visible array from prior adds...
      if (!alreadyAddedSessions.includes(sessionSid)) {
        if (session.location === 'testing') {
          visibleSessionsArray.push({
            sessionSid,
            inRoomAt: session.inRoomAt || 0
          });
        }
      }
    }

    // Stage 3: Sort by inRoomAt (oldest first)
    visibleSessionsArray.sort((a, b) => {
      return a.inRoomAt - b.inRoomAt;
    });

    set({ visibleSessions: visibleSessionsArray });
  },

  /**
   * Adds a session to the visibleSessions array
   * 
   * @function addToVisibleSessions
   * @param {Object} params
   * @param {string} params.sessionSid - The session SID to add
   * @param {number} [params.inRoomAt] - Optional inRoomAt timestamp (defaults to current time)
   * @returns {void}
   */
  addToVisibleSessions: ({ sessionSid, inRoomAt }) => {
    const visibleSessions = get().visibleSessions;

    // Check if session is already in the array
    const exists = visibleSessions.some(
      (session) => session.sessionSid === sessionSid
    );

    if (exists) {
      return; // Already in the array, no need to add
    }

    // Add the session with inRoomAt timestamp
    const newSession = {
      sessionSid,
      inRoomAt: inRoomAt || Date.now()
    };

    // Add to array and re-sort by inRoomAt
    const updatedSessions = [...visibleSessions, newSession];
    updatedSessions.sort((a, b) => a.inRoomAt - b.inRoomAt);

    set({ visibleSessions: updatedSessions });
  },

  /**
   * Removes a session from the visibleSessions array
   * 
   * @function removeFromVisibleSessions
   * @param {Object} params
   * @param {string} params.sessionSid - The session SID to remove
   * @returns {void}
   */
  removeFromVisibleSessions: ({ sessionSid }) => {
    const visibleSessions = get().visibleSessions;

    // Filter out the session
    const updatedSessions = visibleSessions.filter(
      (session) => session.sessionSid !== sessionSid
    );
    set({ visibleSessions: updatedSessions });
  },

  /**
   * Opens the next session that needs to be checked in
   * 
   * Priority order:
   * 1. Oldest reconnect session
   * 2. Oldest waiting session
   * 
   * Implements conflict resolution:
   * - Attempts to claim the session optimistically
   * - Verifies claim succeeded by checking reviewBy field
   * - Retries with exponential backoff if claim failed
   * - Gives up after max retries or no sessions available
   * 
   * @function openNextCheckinSession
   * @param {number} [retryCount=0] - Internal retry counter
   * @param {number} [retryDelay=100] - Delay in ms before retry
   * @returns {Promise<void>}
   */
  openNextCheckinSession: async (retryCount = 0, retryDelay = 100) => {
    const MAX_RETRIES = 5;
    const state = get();
    const currentUserSid = useAppStore.getState()?.user?.sid;
    let nextSessionSid = null;

    // First, check for reconnect sessions (oldest first)
    if (Object.keys(state.reconnect).length > 0) {
      // Get all reconnect sessions and sort by timestamp (oldest first)
      const reconnectSessions = Object.entries(state.reconnect)
        .map(([sid, session]) => ({
          sid,
          timestamp: session.sessionData?.entered || 0
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      if (reconnectSessions.length > 0) {
        nextSessionSid = reconnectSessions[0].sid;
      }
    }

    // If no reconnect sessions, check waiting sessions (oldest first)
    if (!nextSessionSid && Object.keys(state.waiting).length > 0) {
      const waitingSessions = Object.entries(state.waiting)
        .map(([sid, session]) => ({
          sid,
          timestamp: session.sessionData?.entered || 0
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      if (waitingSessions.length > 0) {
        nextSessionSid = waitingSessions[0].sid;
      }
    }

    // No sessions available
    if (!nextSessionSid) {
      if (retryCount === 0) {
        console.log('[VTC] No sessions available for check-in');
      }
      return;
    }

    // Attempt to claim the session by opening drawer
    // This will trigger setFocusedSession -> transitionSession with reviewBy
    // Drawer opens immediately, verification happens in background
    // If claim fails, drawer will auto-close and we'll see it via sync events
    await useAppStore.getState().openDrawer('focus', nextSessionSid);

    // Note: If verification fails, setFocusedSession will close the drawer in background
    // and log which proctor won. No retry needed since drawer already opened.
  },

  handleOnSessionEnded: async ({ sessionSid, reasonId, reasonText, notes, forceCancel }) => {
    //add this to the exitedSessions object
    //get().addToExitedSessions(sessionSid);
  },

  /**
   * Handles sending a session back to onboarding steps
   * 
   * Processes a session that needs to be sent back to one or more onboarding steps
   * during check-in. Updates the proctor interaction and either unfocuses the session
   * (closing the drawer and transitioning) or transitions it to 'sentback' state.
   * 
   * @async
   * @function handleSentBackToOnboardingStep
   * @param {Object} params - Sent back parameters
   * @param {string} params.sessionSid - Session identifier
   * @param {string} params.installSid - Installation identifier
   * @param {string} params.examSid - Exam identifier
   * @param {string[]} params.stepSids - Array of onboarding step identifiers to send back to
   * @param {boolean} [params.unfocusSession=false] - Whether to unfocus the session and close the drawer
   * @returns {Promise<void>}
   * 
   * @example
   * // Send back to steps and unfocus
   * await handleSentBackToOnboardingStep({
   *   sessionSid: 'sess_123',
   *   installSid: 'inst_456',
   *   examSid: 'exam_789',
   *   stepSids: ['step_1', 'step_2'],
   *   unfocusSession: true
   * });
   * 
   * @example
   * // Send back to steps without unfocusing
   * await handleSentBackToOnboardingStep({
   *   sessionSid: 'sess_123',
   *   installSid: 'inst_456',
   *   examSid: 'exam_789',
   *   stepSids: ['step_1']
   * });
   */
  handleSentBackToOnboardingStep: async ({ sessionSid, installSid, examSid, stepSids, unfocusSession = false }) => {
    // Since the session was approved, update the interaction to indicate that checkin has ended...
    await get().updateProctorInteraction(sessionSid, 'sentback-to-onboarding-step');

    // We will want to fire an event that indicates checkin ended in this case...
    // we do not want to await this b/c we just want to fire and forget...
    get().addAnnotation({
      sessionSid,
      data: {
        typeId: 'sentback-to-onboarding',
        metadata: {
          unfocusSession
        }
      }
    });

    // if the session is to be unfocused, then lets do it.
    if (unfocusSession) {
      await get().clearFocusedSession({ closeDrawer: true, transitionSession: 'sentback' });
      // transition the session to sentback since we are unfocusing...
      await get().transitionSession(sessionSid, 'sentback');
    }
  },

  /**
   * Removes a session from the store
   * 
   * Removes a session from its current location bucket. If in remote mode, triggers
   * an API call via the data broker. Otherwise, handles removal locally.
   * 
   * @function remove
   * @param {string} sessionSid - The session identifier to remove
   * @returns {void}
   * 
   * @example
   * // Remove a session
   * useSessionStore.getState().remove('session_123');
   */
  remove: (sessionSid) => {
    get().handleRemoveEvent(sessionSid);
  },

  /**
   * Handles soft removal of a session from the store
   * 
   * Soft removal marks a session as removed without actually removing it from the UI.
   * This allows the Session Viewer to display an overlay notification to the proctor
   * that the session was removed. The session remains in the UI until:
   * 
   * 1. The proctor acknowledges the overlay by clicking a button, OR
   * 2. The overlay times out (handled by the Session Viewer component)
   * 
   * After acknowledgment or timeout, the Session Viewer will call the full `remove()`
   * function to actually remove the session from the UI and clean up all resources.
   * 
   * @function handleSoftRemoval
   * @param {string} sessionSid - The session ID to soft remove
   * 
   * @example
   * // Soft remove a session (shows overlay, keeps in UI)
   * useSessionStore.getState().handleSoftRemoval('session_123');
   * 
   * // Later, after user acknowledgment or timeout:
   * useSessionStore.getState().remove('session_123');
   */
  handleSoftRemoval: (sessionSid, type) => {
    // soft removal is when we are showing the user that the session was removed...but we are not actually removing from the UI yet...

    const sessionViewerRef = get().getSessionViewerRef(sessionSid);
    if (sessionViewerRef) {
      // now get the constants...
      const SESSION_VIEWER_CONSTANTS = sessionViewerRef.getConstants();

      sessionViewerRef.setOverlay({ name: 'session-removed' });
    }



  },

  /**
   * Internal handler for removing a session from the store
   * 
   * This is called by the `remove` function and handles the actual state update
   * to remove the session from its current location bucket. React automatically
   * handles all DOM cleanup when the session is removed from state.
   * 
   * **Cleanup Flow:**
   * 1. Session removed from Zustand state
   * 2. React reconciliation detects session removal
   * 3. SessionViewerPortal unmounts and checks if session still exists
   * 4. If session doesn't exist, portal skips DOM manipulation
   * 5. React cleanly unmounts SessionViewerPool and all child components
   * 6. LiveKit connections are disconnected via component unmount effects
   * 
   * @function handleRemoveEvent
   * @param {string} sessionSid - The session identifier to remove
   * @returns {void}
   * @private
   * 
   * @see SessionViewerPortal - Checks session existence before DOM manipulation
   * @see SessionViewerPool - Renders all SessionViewer instances
   */
  handleRemoveEvent: (sessionSid) => {
    // Get session reference before removing from state
    const session = get().sessions?.[sessionSid];
    if (!session) {
      console.warn(`[SessionStore] Session ${sessionSid} not found, skipping removal`);
      return;
    }

    const sessionState = session.location;

    // CRITICAL: Move element back to pool BEFORE updating state
    // This prevents React from trying to removeChild() from the wrong parent
    const poolContainer = document.getElementById('session-viewer-pool');
    const portalContainer = document.querySelector(`[data-session-portal="${sessionSid}"]`);

    if (poolContainer && portalContainer) {
      const viewerElement = portalContainer.querySelector(`[data-session-viewer="${sessionSid}"]`);
      if (viewerElement) {
        poolContainer.appendChild(viewerElement);
      }
    }

    // clear the flagging alert for this session
    get().clearFlaggingAlerts(sessionSid);

    // Small delay to let DOM settle before React reconciliation
    requestAnimationFrame(() => {
      get().updateStateAfterRemoval(sessionSid, sessionState);
    });
  },

  updateStateAfterRemoval: (sessionSid, sessionState) => {
    set((state) => {
      // Remove from main sessions object
      const { [sessionSid]: removedSession, ...updatedSessions } = state.sessions;

      // Remove from the specific location object where the session exists
      let locationUpdate = {};
      switch (sessionState) {
        case 'waiting': {
          const { [sessionSid]: removed, ...updatedWaiting } = state.waiting;
          locationUpdate = { waiting: updatedWaiting };
          break;
        }
        case 'review': {
          const { [sessionSid]: removed, ...updatedReview } = state.review;
          locationUpdate = { review: updatedReview };
          break;
        }
        case 'testing': {
          const { [sessionSid]: removed, ...updatedTesting } = state.testing;
          locationUpdate = { testing: updatedTesting };
          break;
        }
        case 'checkout': {
          const { [sessionSid]: removed, ...updatedCheckout } = state.checkout;
          locationUpdate = { checkout: updatedCheckout };
          break;
        }
        case 'reconnect': {
          const { [sessionSid]: removed, ...updatedReconnect } = state.reconnect;
          locationUpdate = { reconnect: updatedReconnect };
          break;
        }
        case 'sentback': {
          const { [sessionSid]: removed, ...updatedSentback } = state.sentback;
          locationUpdate = { sentback: updatedSentback };
          break;
        }
        default:
          console.warn(`[SessionStore] Unknown session location: ${sessionState}`);
          break;
      }



      // Return combined update for both sessions and location objects
      return {
        sessions: updatedSessions,
        ...locationUpdate
      };
    });

    get().updateVisibleSessions();
  },

  /**
   * Adds a random dummy session to the store (TESTING ONLY)
       * 
       * Selects a random session from the dummy data that is not already in the store
       * and adds it. This is useful for testing the UI with multiple sessions without
       * needing real exam sessions.
       * 
       * @function addRandom
       * @returns {void}
       * 
       * @example
       * // Add a random test session
       * useSessionStore.getState().addRandom();
       * 
       * @warning This function is for testing/development only and should not be used in production
       */
  addRandom: () => {
    // Get all keys from sourceObject
    const sessionOptions = Object.keys(dummySessions);
    const sessionsInRoom = Object.keys(get().sessions);

    const sessionsInRoomSet = new Set(sessionsInRoom); // Convert arr2 to a Set for O(1) lookup time
    const sessionsRemaining = sessionOptions.filter(
      (value) => !sessionsInRoomSet.has(value)
    );

    const shuffled = [...sessionsRemaining]; // Copy the original array to avoid mutation
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1)); // Pick a random index
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; // Swap elements
    }

    const randomSessionSid = shuffled.shift();

    get().add(randomSessionSid, dummySessions[randomSessionSid]);
  },

  /**
   * Retrieves session data by session ID
   * 
   * Looks up the session's location and returns the full session data object
   * from the appropriate location bucket.
   * 
   * @function getSessionData
   * @param {string} sessionSid - The session identifier
   * @returns {Object|null} The session data object, or null if not found
   * 
   * @example
   * const sessionData = useSessionStore.getState().getSessionData('session_123');
   * console.log(sessionData.studentName);
   */
  getSessionData: (sessionSid) => {
    const location = get().sessions?.[sessionSid]?.location;

    if (location) {
      const sessionData = get()[location]?.[sessionSid];
      return sessionData;
    } else {
      console.error(`Session location not found for session ID: ${sessionSid}`);
      return null;
    }
  },

  getSession: (sessionSid) => {
    const session = get().sessions?.[sessionSid];

    if (session) {
      return session;
    } else {
      console.error(`Session not found for session ID: ${sessionSid}`);
      return null;
    }
  },



  /**
   * Sets the session ID for transfer
   * 
   * Marks a session as pending transfer. This is typically called before showing
   * a transfer confirmation dialog.
   * 
   * @function setTransferSessionSid
   * @param {string} sessionSid - The session identifier to transfer
   * @returns {void}
   * 
   * @example
   * // Mark session for transfer
   * useSessionStore.getState().setTransferSessionSid('session_123');
   */
  setTransferSessionSid: (sessionSid) => {
    set({ transferSessionSid: sessionSid });
  },

  /**
   * Sets the visibility of the transfer note modal.
   * 
   * @function setShowTransferNote
   * @param {boolean} show - Whether to show the modal
   * @returns {void}
   */
  setShowTransferNote: (show) => {
    set({ showTransferNote: show });
  },


  /**
   * Executes a session transfer
   * 
   * Transfers the currently marked session (via transferSessionSid) to another proctor.
   * Removes the session from the store and recalculates layout if the session was in testing.
   * 
   * @async
   * @function transferSession
   * @returns {Promise<void>}
   * 
   * @example
   * // First mark the session for transfer
   * useSessionStore.getState().setTransferSessionSid('session_123');
   * // Then execute the transfer
   * await useSessionStore.getState().transferSession();
   */
  transfer: async (toRoomSid, note) => {
    const sessionSid = get().transferSessionSid;
    // handle transfer....

    //add this to the exitedSessions object
    get().addToExitedSessions(sessionSid);
    const exited = get().exitedSessions;
    const transferResult = await transferSession({
      sessionSid,
      fromRoomSid: useAppStore.getState().roomSid,
      toRoomSid: toRoomSid,
      transferredBySid: useAppStore.getState().user.sid,
      note: note
    });

    if (transferResult.successful) {
      // Get session location before removing
      const sessionData = get().getSessionData(sessionSid);
      const sessionLocation = sessionData?.location;

      // If session is in checkin or testing, keep it focused and let the overlay handle unfocus on acknowledgement
      // Otherwise, unfocus and remove immediately
      if (!['checkin', 'testing', 'review'].includes(sessionLocation)) {
        const stateUpdates = {};

        // if the session was also focused...we need to unfocus the session and close the drawer...
        if (get().focusedSessionSid === sessionSid) {
          stateUpdates.focusedSessionSid = null;
          stateUpdates.focusedSession = null;
          useAppStore.getState().setAllowSounds(true);
          await useAppStore.getState().closeDrawer();
        }

        // do this first so the UI updates before we remove the session...
        if (Object.keys(stateUpdates).length > 0) {
          set(stateUpdates);
        }


        get().remove(sessionSid);
      }

      // only do this if the session was in testing.
      if (sessionLocation === 'testing') {
        useLayoutStore.getState().debouncedLayoutUpdate();
      }
    } else {
      console.error('Transfer failed');
    }


  },

  /**
   * Sets the session ID to end
   * 
   * Marks a session as pending termination. This is typically called before showing
   * an end session confirmation dialog.
   * 
   * @function setEndSessionSid
   * @param {string} sessionSid - The session identifier to end
   * @returns {void}
   * 
   * @example
   * // Mark session for ending
   * useSessionStore.getState().setEndSessionSid('session_123');
   */
  setEndSessionSid: (sessionSid) => {
    set({ endSessionSid: sessionSid });
  },

  /**
   * Adds a session to the exitedSessions object
   * 
   * Creates an in-memory snapshot of the session before it's removed from the main sessions object.
   * This snapshot is retained until the session exit is acknowledged by the proctor.
   * 
   * @function addToExitedSessions
   * @param {string} sessionSid - The session SID to add to exited sessions
   * @returns {void}
   * 
   * @example
   * // Add session to exited sessions before removing it
   * useSessionStore.getState().addToExitedSessions('session_123');
   */
  addToExitedSessions: (sessionSid) => {
    const session = get().sessions[sessionSid];

    if (!session) {
      console.warn(`Cannot add session ${sessionSid} to exitedSessions - session not found`);
      return;
    }

    // Create a snapshot of the full session object
    set((state) => ({
      exitedSessions: {
        ...state.exitedSessions,
        [sessionSid]: { ...session }
      }
    }));
  },

  /**
   * Removes a session from the exitedSessions object
   * 
   * Called when the proctor acknowledges the session exit, cleaning up the in-memory snapshot.
   * 
   * @function removeFromExitedSessions
   * @param {string} sessionSid - The session SID to remove from exited sessions
   * @returns {void}
   * 
   * @example
   * // Remove session from exited sessions after acknowledgement
   * useSessionStore.getState().removeFromExitedSessions('session_123');
   */
  removeFromExitedSessions: (sessionSid) => {
    set((state) => {
      const { [sessionSid]: removed, ...remainingExitedSessions } = state.exitedSessions;
      return { exitedSessions: remainingExitedSessions };
    });
  },

  /**
   * Ends a session
   * 
   * Terminates the currently marked session (via endSessionSid) and removes it from the store.
   * Recalculates layout if the session was in testing.
   * 
   * @async
   * @function endSession
   * @returns {Promise<void>}
   * 
   * @example
   * // First mark the session to end
   * useSessionStore.getState().setEndSessionSid('session_123');
   * // Then end the session
   * await useSessionStore.getState().endSession();
   */
  endSession: async (description) => {
    const sessionSid = get().endSessionSid;

    // ensure there is a session sid set to end...
    if (sessionSid) {
      const session = get().sessions[sessionSid];

      // Call the endSession function on the SessionViewer for this session if ref exists
      if (session?.viewerRef?.current) {
        try {
          const endSessionResponse = await session.viewerRef.current.endSession({
            reasonId: 'proctor-ended',
            reasonText: 'Session ended by proctor',
            notes: description || 'Session ended by proctor'
          });

          if (endSessionResponse.successful) {
            get().setEndSessionSid(null);
            /*
            // now remove session....
            get().remove(sessionSid);
            set({ endSessionSid: null });
            
            // only do this if the session was in testing.
            if (useAppStore.getState()[sessionSid]?.location === 'testing') {
              useLayoutStore.getState().calculateLayoutSizing();
            }
            */
          }
        } catch (error) {
          console.error('Error ending session via viewer:', error);
        }
      }
    }
  },

  /**
   * Get the SessionViewer instance for a specific session
   * 
   * Returns the SessionViewer component instance (ref.current) for the given session.
   * The instance provides access to the SessionViewer's public API methods like
   * setOverlay, getConstants, endSession, etc.
   * 
   * @function getSessionViewerRef
   * @param {string} sessionSid - The session ID to get the viewer instance for
   * @returns {Object|null} The SessionViewer instance or null if not available
   * 
   * @example
   * const viewer = useSessionStore.getState().getSessionViewerRef('session_123');
   * if (viewer) {
   *   viewer.setOverlay({ name: 'session-removed' });
   *   viewer.getConstants();
   * }
   */
  getSessionViewerRef: (sessionSid) => {
    // Validate sessionSid parameter
    if (!sessionSid || typeof sessionSid !== 'string') {
      console.warn('[SessionStore] getSessionViewerRef called with invalid sessionSid:', sessionSid);
      return null;
    }

    // Get sessions object safely
    const sessions = get().sessions;
    if (!sessions || typeof sessions !== 'object') {
      console.warn('[SessionStore] Sessions object is not available');
      return null;
    }

    // Get specific session
    const session = sessions[sessionSid];
    if (!session) {
      console.warn(`[SessionStore] Session not found for sessionSid: ${sessionSid}`);
      return null;
    }

    // Get viewerRef from session
    const viewerRef = session.viewerRef;
    if (!viewerRef) {
      console.warn(`[SessionStore] viewerRef not found for session: ${sessionSid}`);
      return null;
    }

    // Return the current instance, not the ref wrapper
    if (!viewerRef.current) {
      console.warn(`[SessionStore] viewerRef.current not available for session: ${sessionSid}`);
      return null;
    }

    return viewerRef.current;
  },

  /**
   * Clears the currently focused session and handles session state transitions
   * 
   * This function manages the complex logic of unfocusing a session and determining
   * where the session should be transitioned based on its current journey step and
   * whether other proctors are still focused on it.
   * 
   * @async
   * @function clearFocusedSession
   * @param {Object} options - Configuration options
   * @param {boolean} [options.closeDrawer=false] - Whether to close the drawer after unfocusing
   * @param {string} [options.transitionSession] - Explicit transition state (overrides automatic logic)
   * @returns {Promise<void>}
   * 
   * @description
   * **Transition Logic Flow:**
   * 
   * 1. **Explicit Transition (if transitionSession provided)**
   *    - Transitions to the specified state regardless of current journey step
   *    - Use case: Manual override when specific state is required
   * 
   * 2. **Review Location Sessions (location === 'review')**
   *    Handles sessions currently being reviewed by checking journey step:
   * 
   *    a. **Already on WaitingForProctor step**
   *       - Transition: 'waiting'
   *       - Logic: Session is already waiting, just move to waiting queue
   *       - No step changes needed
   * 
   *    b. **Not on ConnectedToProctor step (e.g., sent back for re-verification)**
   *       - Transition: 'sentback'
   *       - Logic: Session was sent back to onboarding steps (ID check, webcam, etc.)
   *       - Adds WaitingForProctor step (if not already present)
   *       - Does NOT auto-advance user (pushUserToNextStep: false)
   *       - Allows proctor to manually advance when ready
   * 
   *    c. **On ConnectedToProctor step BUT no other proctor is focused**
   *       - Transition: 'waiting'
   *       - Logic: Session was connected but this was the only proctor
   *       - Adds WaitingForProctor step and auto-advances user (pushUserToNextStep: true)
   *       - User sees waiting screen immediately
   * 
   *    d. **On ConnectedToProctor step AND another proctor is focused**
   *       - Transition: 'waiting'
   *       - Logic: Another proctor is still reviewing, return to waiting queue
   *       - Adds WaitingForProctor step and auto-advances user (pushUserToNextStep: true)
   *       - User sees waiting screen while other proctor continues review
   * 
   * 3. **Cleanup Actions (all cases)**
   *    - Resets session details tab to 'info'
   *    - Closes incident report modal if open
   *    - Returns video to live view (unless transitioning to checkout)
   *    - Clears focused session state
   *    - Re-enables sound notifications
   *    - Optionally closes drawer
   * 
   * @example
   * // Clear focus and close drawer
   * await useSessionStore.getState().clearFocusedSession({ closeDrawer: true });
   * 
   * @example
   * // Clear focus with explicit transition to checkout
   * await useSessionStore.getState().clearFocusedSession({ 
   *   transitionSession: 'checkout' 
   * });
   * 
   * @note POTENTIAL MISSING CONDITIONS:
   * - What if viewerRef is null/undefined? Currently silently skips transition logic
   * - What if getCurrentJourneyStep() returns null? Falls through to else block
   * - What if session is in 'testing' location? No special handling, just unfocuses
   * - What if session is in 'checkout' location? No special handling, just unfocuses
   * - What if session is in 'reconnect' location? No special handling, just unfocuses
   * - What if isFocusedByAnyProctor check fails/errors? No error handling
   * - What if sendToStep fails? Error is logged but transition still proceeds
   * - Consider: Should 'testing' sessions be prevented from unfocusing?
   * - Consider: Should 'checkout' sessions have special handling?
   */
  clearFocusedSession: async ({ closeDrawer = false, transitionSession } = {}) => {
    const focusedSessionSid = get().focusedSessionSid;

    if (!focusedSessionSid) {
      return;
    }

    // ========================================================================
    // CRITICAL PATH: Immediate UI updates for instant feedback
    // ========================================================================

    const viewerRef = get().sessions[focusedSessionSid]?.viewerRef;
    const sessionData = get().sessions[focusedSessionSid]?.sessionData;
    const focusedSession = get().focusedSession;

    // Check if we are the reviewer by checking session.sessionData.reviewBy
    const currentUserSid = useAppStore.getState()?.user?.sid;
    const wasReviewing = sessionData?.reviewBy === currentUserSid;




    // clear all enabled components, this allows the resizing to be super fast...
    if (viewerRef?.current?.clearComponents) {
      viewerRef.current.clearComponents();
    }

    // 1. Clear focused state FIRST - this triggers UI to update immediately
    set(() => ({ focusedSessionSid: null, focusedSession: null }));
    useAppStore.getState().setAllowSounds(true);

    // 2. Close drawer IMMEDIATELY if requested - don't wait for cleanup
    if (closeDrawer) {
      await useAppStore.getState().closeDrawer();
    }

    // 3. Defer ALL viewer cleanup to not block drawer animation
    // Use setTimeout(0) to push to next event loop tick
    setTimeout(() => {

      if (viewerRef?.current?.closeIncidentReportModal) {
        viewerRef.current.closeIncidentReportModal();
      }
      // Return to live view unless transitioning to checkout
      if (viewerRef?.current?.goToLive && transitionSession !== 'checkout') {
        viewerRef.current.goToLive();
      }
      if (viewerRef?.current?.setMuted) {
        viewerRef.current.setMuted(true);
      }
      // Clear any flagging alerts for this session
      get().clearFlaggingAlerts(focusedSessionSid);
    }, 0);

    // ========================================================================
    // BACKGROUND WORK: Defer expensive operations to not block UI
    // ========================================================================

    // Use setTimeout to defer background work until after drawer animation completes
    // This makes the drawer close feel instant while still handling all necessary logic
    setTimeout(async () => {
      try {
        // Safety check: Verify session still exists before doing background work
        // Session could have been removed if it ended/transferred during drawer close
        if (!get().sessions[focusedSessionSid]) {
          return;
        }

        // Update proctor interaction tracking (API call)
        await get().updateProctorInteraction(focusedSessionSid, 'end-focus');

        // CONDITION 1: Explicit transition provided - use it directly
        if (transitionSession) {
          await get().transitionSession(focusedSessionSid, transitionSession);
        }
        // CONDITION 2: Session is in 'review' location AND we were reviewing - transition back to waiting or reconnect
        else if (focusedSession?.location === 'review' && wasReviewing) {

          if (viewerRef?.current) {
            const currentStep = viewerRef.current.getCurrentJourneyStep();
            const isFocusedByAnyProctor = get().isFocusedByAnyProctor(focusedSessionSid);

            // Determine target queue based on isReconnect flag
            const targetQueue = sessionData?.isReconnect ? 'reconnect' : 'waiting';

            // CONDITION 2a: User is already on WaitingForProctor step
            // Just transition to appropriate queue (reconnect or waiting), no step changes needed
            if (currentStep && currentStep?.stepId === 'WaitingForProctor') {
              await get().transitionSession(focusedSessionSid, targetQueue);
            }
            // CONDITION 2b: User is NOT on ConnectedToProctor (e.g., sent back to ID check, webcam, etc.)
            // Add WaitingForProctor step but don't auto-advance, transition to 'sentback'
            else if (currentStep && currentStep?.stepId !== 'ConnectedToProctor') {
              try {
                await viewerRef.current.sendToStep({
                  stepIds: ['WaitingForProctor'],
                  unfocusSession: false,
                  pushUserToNextStep: false, // Don't auto-advance - let proctor manually advance
                  completeCurrentStep: false
                });
              } catch (error) {
                console.error('Error adding WaitingForProctor step:', error);
              }
              await get().transitionSession(focusedSessionSid, 'sentback');
            }
            // CONDITION 2c: User is on ConnectedToProctor BUT no other proctor is focused
            // Add WaitingForProctor and auto-advance user, transition to appropriate queue
            else if (currentStep?.stepId === 'ConnectedToProctor' && !isFocusedByAnyProctor) {
              await get().transitionSession(focusedSessionSid, targetQueue);
              try {
                await viewerRef.current.sendToStep({
                  stepIds: ['WaitingForProctor'],
                  unfocusSession: false,
                  pushUserToNextStep: true, // Auto-advance user to waiting screen
                  completeCurrentStep: false
                });
              } catch (error) {
                console.error('Error pushing to WaitingForProctor step:', error);
              }
            }
            // CONDITION 2d: Default case - ConnectedToProctor with another proctor focused
            // Add WaitingForProctor and auto-advance user, transition to appropriate queue
            else {
              await get().transitionSession(focusedSessionSid, targetQueue);
              try {
                await viewerRef.current.sendToStep({
                  stepIds: ['WaitingForProctor'],
                  unfocusSession: false,
                  pushUserToNextStep: true, // Auto-advance user to waiting screen
                  completeCurrentStep: false
                });
              } catch (error) {
                console.error('Error pushing to WaitingForProctor step:', error);
              }
            }
          }
        }
        // NOTE: Sessions in 'testing', 'checkout', 'waiting', 'reconnect' locations
        // are not explicitly handled and will just be unfocused without transition

      } catch (error) {
        console.error('Error in clearFocusedSession background work:', error);
      }
    }, 0); // Execute on next tick, after UI has updated
  },

  /**
   * Gets the focused session ID
   * 
   * Returns the session ID of the currently focused session.
   * 
   * @function getFocusedSessionSid
   * @returns {string|null} The focused session ID, or null if no session is focused
   * 
   * @example
   * const focusedSid = useSessionStore.getState().getFocusedSessionSid();
   */
  getFocusedSessionSid: () => get().focusedSessionSid,

  /**
   * Sets the claim failure state
   * 
   * @function setClaimFailure
   * @param {Object} failureData - The claim failure data
   * @param {string} failureData.sessionSid - Session ID that failed to claim
   * @param {string|null} failureData.winnerName - Name of proctor who won (null for timeout)
   * @param {string|null} failureData.winnerSid - SID of proctor who won (null for timeout)
   * @param {string} failureData.message - Message to display to user
   * @param {boolean} failureData.isTimeout - Whether this was a timeout vs lost race
   * 
   * @example
   * // Simulate lost race
   * useSessionStore.getState().setClaimFailure({
   *   sessionSid: 'ES123',
   *   winnerName: 'Jason Fill',
   *   winnerSid: 'PUb123',
   *   message: 'This session was claimed by Jason Fill',
   *   isTimeout: false
   * });
   * 
   * @example
   * // Simulate timeout
   * useSessionStore.getState().setClaimFailure({
   *   sessionSid: 'ES123',
   *   winnerName: null,
   *   winnerSid: null,
   *   message: 'Session claim verification timed out',
   *   isTimeout: true
   * });
   */
  setClaimFailure: (failureData) => set({
    claimFailure: {
      ...failureData,
      timestamp: failureData.timestamp || Date.now()
    }
  }),

  /**
   * Clears the claim failure state
   * 
   * @function clearClaimFailure
   * 
   * @example
   * useSessionStore.getState().clearClaimFailure();
   */
  clearClaimFailure: () => set({ claimFailure: null }),

  /**
   * Transitions a session to a different state/location
   * 
   * @param {string} sessionSid - The session identifier
   * @param {string} toState - The target state. Valid values: "waiting", "review", "testing", "checkout", "reconnect"
   * @param {Object} [data] - Optional data to include with the transition
   * @param {string} [data.reviewBy] - User SID of the proctor reviewing the session (only used when toState is 'review')
   * @returns {void}
   * 
   * @example
   * // Move session from waiting to review
   * useSessionStore.getState().transitionSession('session_123', 'review');
   * 
   * @example
   * // Move session from waiting to review with reviewer assignment
   * useSessionStore.getState().transitionSession('session_123', 'review', { 
   *   reviewBy: 'proctor_sid_123' 
   * });
   * 
   * @example
   * // Move session from review to testing
   * useSessionStore.getState().transitionSession('session_123', 'testing');
   */
  transitionSession: async (sessionSid, toState, data) => {

    // ensure the sessionSid is not null
    if (!sessionSid) {
      return;
    }

    if (useDataBroker.getState().mode === 'remote') {

      const dataToPersist = {
        location: toState,
      };

      // ensure we are updating the review by properly...
      if (data?.reviewBy && toState === 'review') {
        dataToPersist.reviewBy = data.reviewBy;
      } else {
        dataToPersist.reviewBy = null;
      }

      // if we are tranisitioning the state to checkout, we do not want to persist this data as this means they are no longer in the remote document...
      await useDataBroker.getState().persist(sessionSid, dataToPersist);

      // The sync document will trigger an update that calls handleTransitionEvent
      // Don't call it here to avoid race conditions

      // if this is the focused session, clear focus
      /*
      if (get().focusedSessionSid === sessionSid && toState === 'waiting') {
        get().clearFocusedSession();
        useAppStore.getState().closeDrawer();
      }
      */

    } else {
      get().handleTransitionEvent(sessionSid, toState);
    }

  },

  removeFromCheckoutQueue: async (sessionSid) => {
    // Optimistically update local state immediately for better UX
    set((state) => {
      const { [sessionSid]: removed, ...updatedCheckout } = state.checkout;
      return { checkout: updatedCheckout };
    });

    // Then call the data broker to remove the session from checkout
    // The sync doc update will confirm the change
    await useDataBroker.getState().remove(`sessions.${sessionSid}`);
    return true;
  },

  /**
   * Updates a proctor's interaction state for a specific session
   * 
   * Tracks proctor focus and review status in the sync document's userInteractions.
   * This allows multiple proctors to see who is focused on or reviewing a session.
   * 
   * @async
   * @function updateProctorInteraction
   * @param {string} sessionSid - The session identifier
   * @param {string} interaction - The interaction type. Valid values:
   *   - 'start-focus': Proctor is viewing the session (sets isFocused: true)
   *   - 'start-review': Proctor is actively reviewing/checking in (sets inReview: true, isFocused: true)
   *   - 'end-focus': Proctor stopped viewing (clears isFocused and inReview)
   *   - 'start-audio-chat': Proctor started talking (sets isTalking: true)
   *   - 'end-audio-chat': Proctor stopped talking (sets isTalking: false)
   * @returns {Promise<void>}
   * 
   * @example
   * // Mark proctor as reviewing a session
   * await useSessionStore.getState().updateProctorInteraction('ES123', 'start-review');
   * 
   * @example
   * // Mark proctor as just viewing (not reviewing)
   * await useSessionStore.getState().updateProctorInteraction('ES123', 'start-focus');
   * 
   * @example
   * // Clear proctor's focus
   * await useSessionStore.getState().updateProctorInteraction('ES123', 'end-focus');
   */
  updateProctorInteraction: async (sessionSid, interaction) => {
    // ensure the sessionSid is not null
    if (!sessionSid) {
      return;
    }

    // before we perist anything, ensure the session is valid, for example if it was transfered out it will no longer exist...
    if (!get().sessions?.[sessionSid]) {
      return;
    }

    // if we are cleaning up this session, do not do anything...
    if (sessionSid === get().cleaningUpSessionSid) {
      return;
    }

    // if the session is in checkout, don't do anything...
    if (get().sessions?.[sessionSid]?.location === 'checkout') {
      return;
    }

    if (useDataBroker.getState().mode === 'remote') {
      const data = {};

      switch (interaction) {
        case 'start-focus':
          data.isFocused = true;
          data.isFocusedChangeAt = Date.now();
          // We will want to fire an event that indicates checkin ended in this case...
          // we do not want to await this b/c we just want to fire and forget...
          get().addAnnotation({
            sessionSid,
            data: {
              typeId: 'focus-start'
            }
          });
          break;
        case 'end-focus':

          // if the session was also in review status, update that...
          if (get().sessions?.[sessionSid]?.location === 'review') {
            data.inReview = false;
            data.isInReviewChangeAt = Date.now();
            data.isFocused = false;
            data.isFocusedChangeAt = Date.now();
            get().addAnnotation({
              sessionSid,
              data: {
                typeId: 'checkin-end'
              }
            });
          } else {
            data.isFocused = false;
            data.isFocusedChangeAt = Date.now();
            data.inReview = false;
            data.isInReviewChangeAt = Date.now();
            data.isTalking = false;
            data.isTalkingChangeAt = Date.now();
            // we do not want to await this b/c we just want to fire and forget...
            get().addAnnotation({
              sessionSid,
              data: {
                typeId: 'focus-end'
              }
            });
          }

          break;
        case 'start-review':
          data.inReview = true;
          data.isFocused = true;
          data.isInReviewChangeAt = Date.now();
          data.isFocusedChangeAt = Date.now();
          // we do not want to await this b/c we just want to fire and forget...
          get().addAnnotation({
            sessionSid,
            data: {
              typeId: 'checkin-start'
            }
          });
          break;

        case 'start-audio-chat':
          data.isTalking = true;
          data.isTalkingChangeAt = Date.now();
          break;

        case 'end-audio-chat':
          data.isTalking = false;
          data.isTalkingChangeAt = Date.now();
          break;

        case 'approved':
          data.inReview = false;
          data.isInReviewChangeAt = Date.now();
          data.approved = true;
          data.approvedAt = Date.now();
          // we do not want to await this b/c we just want to fire and forget...
          get().addAnnotation({
            sessionSid,
            data: {
              typeId: 'checkin-approved'
            }
          });
          get().addAnnotation({
            sessionSid,
            data: {
              typeId: 'checkin-end'
            }
          });
          get().addAnnotation({
            sessionSid,
            data: {
              typeId: 'focus-start'
            }
          });
          break;
        case 'sentback-to-onboarding-step':
          data.sentBack = true;
          data.sentBackAt = Date.now();
          // An annotation was created upstream so nothing needs to be done here...
          break;
        default:
          break;
      }

      const interactionData = {
        firstName: useAppStore.getState().user.firstName,
        lastName: useAppStore.getState().user.lastName,
        ...data
      };

      const sessionViewerRef = get().getSessionViewerRef(sessionSid);
      if (sessionViewerRef?.updateUserInteraction) {
        await sessionViewerRef.updateUserInteraction(interactionData);
      } else {
        //TODO: Add a fallback (or error) for when the session viewer ref is not available...
        console.log('Session viewer ref not available for session, cannot update user interaction: ' + sessionSid);
      }
    }
  },

  isFocusedByAnyProctor: (sessionSid) => {
    const userInteractions = get().userInteractions?.[sessionSid];
    if (!userInteractions) return false;

    // Check if any proctor has isFocused set to true
    return Object.values(userInteractions).some(interaction =>
      interaction.type === 'proctor' &&
      interaction.isFocused === true
    );
  },

  /**
   * Internal handler for session state transitions
   * 
   * This is called by `transitionSession` and handles the actual state update
   * to move a session from one location bucket to another.
   * 
   * @function handleTransitionEvent
   * @param {string} sessionSid - The session identifier
   * @param {string} toState - The target state
   * @returns {void}
   * @private
   */
  handleTransitionEvent: (sessionSid, toState) => {
    set((state) => {
      const location = get().sessions?.[sessionSid]?.location;
      // If session doesn't exist or is already in the target state, no update needed
      if (!location || location === toState) {
        return {};
      }

      const sessionData = state[location]?.[sessionSid];

      if (!sessionData) {
        return {};
      }

      // Update session location metadata
      const updatedSessions = {
        ...state.sessions,
        [sessionSid]: {
          ...state.sessions[sessionSid],
          location: toState,
        },
      };

      // Update session data location
      const updatedSessionData = {
        ...sessionData,
        location: toState,
      };

      // Only update the two location objects that are affected: source and destination
      const stateUpdates = {
        sessions: updatedSessions,
      };

      // Remove from source location
      const { [sessionSid]: removed, ...updatedSourceLocation } = state[location];
      stateUpdates[location] = updatedSourceLocation;

      // Add to destination location
      stateUpdates[toState] = {
        ...state[toState],
        [sessionSid]: updatedSessionData,
      };

      // Update testingCount if transitioning to or from testing
      if (toState === 'testing') {
        stateUpdates.testingCount = Object.keys(state.testing).length + 1;
      } else if (location === 'testing') {
        stateUpdates.testingCount = Math.max(0, Object.keys(state.testing).length - 1);
      }

      //TODO: Event emitters here maybe......
      return stateUpdates;
    });

    get().updateVisibleSessions();

    // Recalculate layout if transitioning to testing (debounced)
    if (toState === 'testing') {
      useLayoutStore.getState().debouncedLayoutUpdate();
    }

    // if this session is the focused session, make sure we update the focused session data...
    if (get().focusedSessionSid === sessionSid) {
      const sessionData = get()[toState]?.[sessionSid];

      if (sessionData) {
        set({
          focusedSession: sessionData,
          focusedSessionSid: sessionSid
        });
      }
    }
  },

  invokeSessionAction: async ({ sessionSid, data }) => {
    const session = get().getSession(sessionSid);
    if (session?.viewerRef?.current) {
      try {
        return await session.viewerRef.current.invokeAction(data);
      } catch (error) {
        console.error('Failed to invoke action:', error);
        return null;
      }
    }
    return null;
  },

  /**
   * Gets the proctor who is actively reviewing a session
   * 
   * Finds the proctor with inReview: true in the session's userInteractions.
   * Only one proctor should have inReview: true at a time (the primary reviewer).
   * 
   * @function activelyCheckingInBy
   * @param {string} sessionSid - The session identifier
   * @returns {Object|null} Proctor object with userSid property, or null if no one is reviewing
   * 
   * @example
   * const reviewer = useSessionStore.getState().activelyCheckingInBy('ES123');
   * if (reviewer) {
   *   console.log('Reviewed by:', reviewer.firstName, reviewer.lastName);
   *   console.log('Reviewer SID:', reviewer.userSid);
   * }
   */
  activelyCheckingInBy: (sessionSid) => {
    // get the userInteractions for this session, and figure out who is checking in 
    const userInteractions = get().userInteractions?.[sessionSid];

    if (!userInteractions) {
      return null;
    }

    const proctorEntry = Object.entries(userInteractions).find(
      ([key, interaction]) => interaction.type === 'proctor' && interaction.inReview === true
    );

    if (!proctorEntry) {
      return null;
    }

    const [proctorUserSid, proctor] = proctorEntry;
    return {
      ...proctor,
      userSid: proctorUserSid
    };
  },

  /**
   * Sets a session as focused
   * 
   * Focuses on a specific session, typically to view its details or interact with it.
   * If the session is in the waiting state, automatically transitions it to review.
   * Disables sound notifications while a session is focused.
   * 
   * Implements conflict resolution for manual pickup:
   * - Attempts to claim session by setting reviewBy field
   * - Verifies claim succeeded after sync document update
   * - Closes drawer if another proctor claimed it first
   * 
   * @function setFocusedSession
   * @param {string} sessionSid - The session identifier to focus
   * @param {boolean} [skipConflictCheck=false] - Skip conflict verification (used internally for retries)
   * @returns {Promise<boolean>} True if successfully focused, false if claim failed
   * 
   * @example
   * // Focus on a session to view details
   * const success = await useSessionStore.getState().setFocusedSession('session_123');
   */
  setFocusedSession: async (sessionSid, skipConflictCheck = false) => {
    const session = get().getSession(sessionSid);
    const viewerRef = get().sessions[sessionSid]?.viewerRef;
    const currentUserSid = useAppStore.getState()?.user?.sid;
    const wasInWaitingOrReconnect = session.location === 'waiting' || session.location === 'reconnect';

    // if there is already a focused session, ensure it is properly cleared first...
    if (get().focusedSessionSid !== sessionSid) {
      await get().clearFocusedSession();
    }

    // If the focused session is currently in the waiting stage, we need to move them to in review area...
    if (session.location === 'waiting' || session.location === 'reconnect') {
      // CONFLICT RESOLUTION: Verify claim in background (don't block drawer opening)
      // This ensures only one proctor can successfully claim a session when multiple proctors
      // attempt to claim simultaneously. Uses optimistic locking with event-based verification.
      if (!skipConflictCheck) {
        // STEP 1: Set up event listener BEFORE calling transitionSession
        // This is critical - listener must be attached before transition to catch the event
        const verificationPromise = new Promise((resolve) => {
          // Safety timeout: If STUDENT_TRANSITIONED event doesn't fire within 2 seconds,
          // assume failure (network issues or sync document problems)
          const timeout = setTimeout(() => {
            cleanup();
            console.log(`[VTC DEBUG] Timeout waiting for transition event for session ${sessionSid}`);
            resolve(false); // Timeout = verification failed
          }, 2000);

          // Event handler: Fires when ANY session transitions in the room
          // We filter for our specific session transitioning to 'review'
          const transitionHandler = (student) => {
            console.log(`[VTC DEBUG] Transition event received - sessionSid: ${student.sessionSid}, location: ${student.data.location}`);

            // Is this OUR session transitioning to review?
            if (student.sessionSid === sessionSid && student.data.location === 'review') {
              cleanup(); // Remove listener and timeout

              // VERIFICATION: Check if WE won the race by comparing reviewBy field
              // The sync document uses last-write-wins, so only one proctor's reviewBy will persist
              const session = get().getSession(sessionSid);
              const reviewBy = session?.sessionData?.reviewBy;
              console.log(`[VTC DEBUG] Claim check - currentUserSid: ${currentUserSid}, reviewBy: ${reviewBy}, match: ${reviewBy === currentUserSid}`);

              const won = reviewBy === currentUserSid;
              resolve(won); // true = we won, false = another proctor won
            }
          };

          // Cleanup function: Removes event listener and clears timeout
          const cleanup = () => {
            clearTimeout(timeout);
            useDataBroker.getState().sync.emitter?.off(PROCTOR_ROOM_EVENTS.STUDENT_TRANSITIONED, transitionHandler);
          };

          // ATTACH LISTENER NOW - before transition happens
          useDataBroker.getState().sync.emitter?.on(PROCTOR_ROOM_EVENTS.STUDENT_TRANSITIONED, transitionHandler);
        });

        // STEP 2: Trigger the transition - listener is already attached and ready
        // This writes to sync document with reviewBy = currentUserSid
        // If multiple proctors do this simultaneously, sync document will resolve conflict
        await get().transitionSession(sessionSid, 'review', { reviewBy: currentUserSid });

        // STEP 3: Run verification in background WITHOUT awaiting
        // This allows the function to continue immediately (drawer opens right away)
        // Verification happens asynchronously and updates UI if claim fails
        (async () => {
          const verificationResult = await verificationPromise;

          if (verificationResult === false) {
            // Verification failed (either timeout or lost race)
            const session = get().getSession(sessionSid);
            const winnerSid = session?.sessionData?.reviewBy;

            // Determine failure type: timeout vs lost race
            if (!winnerSid || winnerSid === currentUserSid) {
              // TIMEOUT SCENARIO: Event never fired or reviewBy matches us but verification timed out
              // This indicates network issues or sync document problems
              console.log(`[VTC] Session ${sessionSid} claim verification timed out`);

              set({
                claimFailure: {
                  sessionSid,
                  winnerName: null,
                  winnerSid: null,
                  timestamp: Date.now(),
                  message: 'Session claim verification timed out',
                  isTimeout: true
                }
              });
            } else {
              // LOST RACE SCENARIO: Another proctor's reviewBy persisted in sync document
              // This means they won the race condition - their write arrived last
              const proctors = useAppStore.getState().proctors;
              const winner = proctors[winnerSid];
              const winnerName = winner ? `${winner.firstName} ${winner.lastName}` : 'Another proctor';

              console.log(`[VTC] Session ${sessionSid} was claimed by another proctor: ${winnerName} (${winnerSid})`);

              set({
                claimFailure: {
                  sessionSid,
                  winnerName,
                  winnerSid,
                  timestamp: Date.now(),
                  message: `This session was claimed by ${winnerName}`,
                  isTimeout: false
                }
              });
            }
          }
          // If verificationResult === true, claim succeeded - do nothing, drawer stays open
        })();
      }

      // Note: handleSessionReady will fire and check session.reviewBy to determine if we won the race
      // Only the proctor whose reviewBy persisted will get inReview: true
    } else if (session.location === 'review') {
      // Session is already in review - check if viewer is ready and update interaction immediately
      const sessionViewerRef = get().getSessionViewerRef(sessionSid);
      if (sessionViewerRef?.updateUserInteraction) {
        // Viewer is ready, update interaction now
        const proctors = useAppStore.getState().proctors;

        // Check the session's reviewBy field - this is the source of truth for who is reviewing
        // If reviewBy is set and that proctor is online, they are the reviewer
        const reviewerIsOnline = session.sessionData?.reviewBy && proctors[session.sessionData.reviewBy];

        if (!session.sessionData?.reviewBy || !reviewerIsOnline || session.sessionData?.reviewBy === currentUserSid) {
          // No reviewer assigned, or reviewer is offline, or it's us - we become the reviewer
          await get().updateProctorInteraction(sessionSid, 'start-review');
        } else {
          // Someone else is the assigned reviewer and they're online - we just focus
          await get().updateProctorInteraction(sessionSid, 'start-focus');
        }
      }
      // If viewer not ready yet, handleSessionReady will handle it
    } else {
      // For other locations (testing, etc.), update interaction if viewer is ready
      const sessionViewerRef = get().getSessionViewerRef(sessionSid);
      if (sessionViewerRef?.updateUserInteraction) {
        await get().updateProctorInteraction(sessionSid, 'start-focus');
      }
      // If viewer not ready yet, handleSessionReady will handle it
    }

    if (viewerRef?.current?.setMuted) {
      viewerRef.current.setMuted(false);
    }

    // Determine if current user is the reviewer by checking session.reviewBy field
    // This is the source of truth for who should have full review permissions
    const isReviewer = session.sessionData?.reviewBy === currentUserSid;

    // Build sessionDetails tabs array based on session location and reviewer status
    // - 'info' and 'identity' tabs are always included
    // - 'flags' tab only for reviewers in review location (not for secondary viewers)
    // - 'checkin' tab only for sessions in waiting location
    // - 'communication' and 'incidentReport' tabs are always included
    const tabs = ['info', 'identity'];
    let defaultTab = 'info';

    // Add flags tab and set as default for primary reviewers
    if (session.location === 'review' && isReviewer) {
      //tabs.push('flags');
      //defaultTab = 'flags';
      tabs.push('checkin');
      defaultTab = 'checkin';
    }

    // Add checkin tab and set as default for waiting sessions
    if (session.location === 'waiting') {
      tabs.push('checkin');
      defaultTab = 'checkin';
    }

    if (session.location === 'testing') {
      tabs.push('flags');
      defaultTab = 'flags';
    }

    tabs.push('communication', 'incidentReport');

    // Configure enabled components for the session viewer
    const components = {
      sessionDetails: {
        defaultTab,
        defaultCollapsed: false,
        tabs: tabs
      },
      video: {
        sources: ['live', 'recorded'],
        default: 'live'
      },
      controls: true,
      timeline: true,
      settings: true,
      chat: true,
      microphone: true,
    };

    if (viewerRef?.current?.enableComponents) {
      viewerRef.current.enableComponents(components);

      // Expand the SessionDetails pane using the public API
      if (viewerRef.current?.expandSessionDetails) {
        viewerRef.current.expandSessionDetails();
      }
      // now set the default tab
      if (viewerRef.current?.setSessionDetailsTab) {
        viewerRef.current.setSessionDetailsTab(defaultTab);
      }
    }

    /*
    Send an annotation that will lower the hand, this is done via an annotation
     - the function handles not sending if not raised...
     - we are not going to await this as we want it to be fire and forget (non-blocking)
    */
    get().lowerRaisedHand({ sessionSid, reason: "Entered Focus Mode" });

    // Clear any flagging alerts for this session
    get().clearFlaggingAlerts(sessionSid);

    // Get the session data AFTER transition to ensure we have the updated location
    const updatedLocation = get().sessions?.[sessionSid]?.location;
    const sessionData = get()[updatedLocation]?.[sessionSid];

    set({
      focusedSession: sessionData,
      focusedSessionSid: sessionSid,
    });
    useAppStore.getState().setAllowSounds(false);
    // call to makes sure we optimize the layout.

    // Also trigger layout store update
    useLayoutStore.getState().calculateLayoutSizing();

    return true;
  },

  lowerRaisedHand: async ({ sessionSid, reason }) => {
    const session = get().getSession(sessionSid);

    if (session?.viewerRef?.current) {
      await get().addAnnotation({
        sessionSid,
        data: {
          typeId: 'hand-lowered',
          metadata: { reason }
        }
      });
    }
  },

  openSessionLog: (sessionSid) => {
    const session = get().getSession(sessionSid);

    if (session?.viewerRef?.current) {
      session.viewerRef.current.openSessionLog();
    }
  },

  openDiagnostics: (sessionSid) => {
    const session = get().getSession(sessionSid);

    if (session?.viewerRef?.current) {
      session.viewerRef.current.openDiagnostics();
    }
  },

  addAnnotation: async ({ sessionSid, data }) => {
    const session = get().getSession(sessionSid);

    if (session?.viewerRef?.current) {
      if (!data.createdById) {
        data.createdById = useAppStore.getState()?.user?.sid;
      }
      try {
        return await session.viewerRef.current.addAnnotation(data);
      } catch (error) {
        console.error('Failed to add annotation:', error);
        return null;
      }
    }
    return null;
  },

  /**
   * Reinitialize a session's SessionViewer component
   * 
   * This calls the reinitialize() method on the SessionViewer ref, which performs
   * a complete reinitialization including resetting stores, reconnecting LiveKit,
   * and reloading session data. Useful when media connections get stuck.
   * 
   * @function reinitializeSession
   * @param {string} sessionSid - The session identifier to reinitialize
   * @returns {Promise<void>}
   * 
   * @example
   * // Reinitialize a stuck session
   * await useSessionStore.getState().reinitializeSession('session_123');
   */
  reinitializeSession: async (sessionSid) => {
    const session = get().getSession(sessionSid);

    if (!session) {
      console.warn(`[SessionStore] Cannot reinitialize session ${sessionSid}: session not found`);
      return;
    }

    if (!session.viewerRef?.current) {
      console.warn(`[SessionStore] Cannot reinitialize session ${sessionSid}: viewerRef not available`);
      return;
    }

    if (typeof session.viewerRef.current.reinitialize !== 'function') {
      console.warn(`[SessionStore] Cannot reinitialize session ${sessionSid}: reinitialize method not available`);
      return;
    }

    try {
      await session.viewerRef.current.reinitialize();
    } catch (error) {
      console.error(`[SessionStore] Failed to reinitialize session ${sessionSid}:`, error);
      throw error;
    }
  },

  remoteDataUpdated: (current, previous) => {

    /*
     First we need to loop through the current data in this store and do a deep compare with what we have stored, if there is a change update our local store.
     Keep in mind we need to update it in the existing location and also the main data area...
     */
    Object.entries(get().sessions).forEach(([sessionSid, sessionData]) => {
      if (JSON.stringify(sessionData) !== JSON.stringify(current?.sessions?.[sessionSid])) {
        set({
          sessions: {
            ...get().sessions,
            [sessionSid]: {
              ...sessionData,
              sessionData: current?.sessions?.[sessionSid],
            },
          }
        });
      }
    });

    // Check for sessions that were removed from the remote data (e.g., removed from checkout queue)
    // This handles the case where a session is deleted from the sync document in another proctor room
    Object.keys(get().checkout).forEach((sessionSid) => {
      // If the session exists in our local checkout but not in the remote data, remove it
      if (!current?.sessions?.[sessionSid]) {
        set((state) => {
          const { [sessionSid]: removed, ...updatedCheckout } = state.checkout;
          return { checkout: updatedCheckout };
        });
      }
    });

    // update the visible sessions
    get().updateVisibleSessions();
  },
  /**
   * Gets the currently focused session data
   * 
   * Returns the full session data object for the currently focused session.
   * 
   * @function getFocusedSession
   * @returns {Object|null} The focused session data, or null if no session is focused
   * 
   * @example
   * const focusedSession = useSessionStore.getState().getFocusedSession();
   * if (focusedSession) {
   *   console.log('Student:', focusedSession.studentName);
   * }
   */
  getFocusedSession: async () => {
    const sessionSid = get().focusedSessionSid;

    const location = get().sessions?.[sessionSid]?.location;
    const sessionData = get()[location]?.[sessionSid];
    return sessionData;
  },

  /**
   * Persists changes to the backend
   * 
   * Placeholder function for future implementation of persisting session changes
   * to the backend/database.
   * 
   * @async
   * @function persistChange
   * @returns {Promise<void>}
   * @todo Implement backend persistence logic
   */
  persistChange: async () => { },
}));

// ============================================================================
// EXPORTS & GLOBAL ACCESS
// ============================================================================

/**
 * Global store access for debugging
 * @global
 * @example
 * // Access store in browser console
 * window.store.getState().sessions
 */
window.store = useSessionStore;

export default useSessionStore;

// ============================================================================
// SESSION LIFECYCLE DOCUMENTATION
// ============================================================================

/**
 * @overview Session State Flow
 * 
 * Sessions in the Virtual Test Center move through the following states:
 * 
 * 1. **waiting** - Student has joined and is waiting for a proctor
 *    - Sound notification plays when session enters this state (after initial load)
 *    - Session appears in the waiting queue
 * 
 * 2. **review** - Proctor is reviewing the session
 *    - Automatically transitions from waiting when proctor focuses on session
 *    - Proctor can review student info, exam details, etc.
 * 
 * 3. **testing** - Student is actively taking the exam
 *    - Proctor is monitoring the session
 *    - Video/audio connections are active
 *    - Layout sizing is calculated for this state
 * 
 * 4. **checkout** - Exam is completed
 *    - Student has finished the exam
 *    - Final review and checkout process
 * 
 * 5. **reconnect** - Session is reconnecting
 *    - Temporary state when connection is lost
 *    - Session will return to previous state when reconnected
 * 
 * @example
 * // Typical session flow
 * add(sid, { location: 'waiting' })           // Student joins
 *   ↓
 * setFocusedSession(sid)                      // Proctor clicks on session
 *   ↓ (auto-transitions to 'review')
 * transitionSession(sid, 'testing')           // Proctor starts exam
 *   ↓
 * transitionSession(sid, 'checkout')          // Exam completes
 *   ↓
 * remove(sid)                                 // Session ends
 * 
 * @example
 * // Session with media connection
 * add(sid, { location: 'waiting' })
 * setFocusedSession(sid)
 * transitionSession(sid, 'testing')
 * // ... exam in progress ...
 * transitionSession(sid, 'checkout')
 * remove(sid)
 */

/**
 * @overview Store Architecture
 * 
 * The session store uses a bucket-based architecture where sessions are organized
 * by their current state/location:
 * 
 * ```
 * sessions: {
 *   'session_123': { location: 'testing' },
 *   'session_456': { location: 'waiting' }
 * }
 * 
 * waiting: {
 *   'session_456': { studentName: 'John', examName: 'Math 101', ... }
 * }
 * 
 * testing: {
 *   'session_123': { studentName: 'Jane', examName: 'Science 201', ... }
 * }
 * ```
 * 
 * **Key Design Decisions:**
 * 
 * 1. **Dual Storage**: Sessions are stored in both `sessions` (for quick lookup)
 *    and location buckets (for easy filtering/display)
 * 
 * 2. **Immutable Updates**: All state updates create new objects to ensure
 *    React re-renders work correctly
 * 
 * 3. **Sound Notifications**: Only play for new waiting sessions after initial
 *    load to avoid notification spam on page load
 * 
 * 4. **Focus Management**: Focusing a session disables sound notifications to
 *    prevent distractions while working with a specific session
 * 
 * 5. **Remote vs Local**: Store supports both local (dummy data) and remote
 *    (API) modes via the data broker
 * 
 * @example
 * // Access sessions by location
 * const waitingSessions = useSessionStore((state) => state.waiting);
 * const testingSessions = useSessionStore((state) => state.testing);
 * 
 * @example
 * // Get specific session data
 * const sessionData = useSessionStore.getState().getSessionData('session_123');
 * 
 * @example
 * // Subscribe to testing count changes
 * const testingCount = useSessionStore((state) => state.testingCount);
 */

/**
 * @overview Integration Points
 * 
 * The session store integrates with several other stores and systems:
 * 
 * **App Store** (`useAppStore`):
 * - `allowSounds` - Controls sound notification playback
 * - `calculateLayoutSizing()` - Recalculates layout when sessions change in testing state
 * 
 * **Data Broker** (`useDataBroker`):
 * - `mode` - Determines if using local dummy data or remote API
 * - `persist()` - Persists session changes to backend in remote mode
 * 
 * **LiveKit Media**:
 * - Media connection bundles stored per session
 * - Used for video/audio proctoring during testing
 * 
 * @example
 * // Typical component usage
 * function SessionQueue() {
 *   const waiting = useSessionStore((state) => state.waiting);
 *   const setFocused = useSessionStore((state) => state.setFocusedSession);
 *   
 *   return (
 *     <div>
 *       {Object.entries(waiting).map(([sid, data]) => (
 *         <SessionCard
 *           key={sid}
 *           data={data}
 *           onClick={() => setFocused(sid)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 */

